import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, of, takeUntil, tap } from 'rxjs';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  DynamicQueryService,
} from '@services/analytics-services/core';
import { ShippingAnalyticsService, ShippingStats } from '@services/analytics-services/shipping';
import {
  GroupedDataState,
  GroupedDataStoreService,
} from '@services/analytics-services/core/grouped-data-store.service';
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { ProcessingState, DateRangePresetType, Pagination } from '@services/core-services/models';
import { DashboardStore } from '../dashboard/dashboard.store';
import { ChartType, QueryType } from '@services/analytics-services/core/models/matrix-stats';
import {
  GroupChartTabs,
  GroupedTabData,
} from '@services/analytics-services/core/group-chart.modals';

@Injectable()
export class DashboardShipmentStore implements OnDestroy {
  private destroy$ = new Subject<void>();
  private shippingAnalytics = new BehaviorSubject<AnalyticsInfo<ShippingStats> | undefined>(
    undefined
  );
  private stateWiseShipmentPerformaceState: GroupedDataState;
  private shippingAnalyticsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private errorMessage = new BehaviorSubject<string>('');

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly shippingAnalyticsService: ShippingAnalyticsService,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly groupedDataStoreService: GroupedDataStoreService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService
  ) {
    this.stateWiseShipmentPerformaceState =
      this.groupedDataStoreService.createGroupedDataState('state'); // Assuming 'state' is the default dimension
    this.dashboardStore
      .getRangeContext()
      .pipe(
        tap(context => {
          this.errorMessage.next('');
          this.loadShippingAnalytics(context).subscribe();
          this.loadStateWiseShipmentPerformanceData(context).subscribe();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getShippingAnalytics(): Observable<AnalyticsInfo<ShippingStats> | undefined> {
    return this.shippingAnalytics.asObservable();
  }

  getShippingAnalyticsLoader(): Observable<ProcessingState> {
    return this.shippingAnalyticsLoader.asObservable();
  }

  getStateWiseShipmentPerformanceStats(): Observable<GroupedTabData> {
    return this.stateWiseShipmentPerformaceState.stats.asObservable();
  }

  getStateWiseShipmentPerformanceTabs(): Observable<GroupChartTabs[]> {
    return this.stateWiseShipmentPerformaceState.tabs.asObservable();
  }

  getStateWiseShipmentPerformanceTabsActiveTab(): Observable<string> {
    return this.stateWiseShipmentPerformaceState.defaultTab.asObservable();
  }

  getStateWiseShipmentPerformancePagination(): Observable<Pagination> {
    return this.stateWiseShipmentPerformaceState.pagination.asObservable();
  }

  getStateWiseShipmentPerformanceLoader(): Observable<ProcessingState> {
    return this.stateWiseShipmentPerformaceState.loader.asObservable();
  }

  getErrorMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  // --- Loading Methods ---
  loadShippingAnalytics(
    context: AnalyticsRangeContext
  ): Observable<AnalyticsInfo<ShippingStats> | undefined> {
    this.shippingAnalyticsLoader.next(ProcessingState.Loading);
    const { range, compare, interval, preset } = context;
    return this.shippingAnalyticsService
      .getAnalytics({
        preset: preset as DateRangePresetType,
        range,
        compare,
        interval,
      })
      .pipe(
        tap(analytics => {
          this.shippingAnalyticsLoader.next(ProcessingState.Success);
          this.shippingAnalytics.next(analytics);
        }),
        catchError(error => {
          this.shippingAnalyticsLoader.next(ProcessingState.Error);
          this.handleError('Error loading shipping analytics', error);
          this.shippingAnalytics.next(undefined);
          return of(undefined);
        })
      );
  }

  loadStateWiseShipmentPerformanceData(context: AnalyticsRangeContext): Observable<any> {
    this.stateWiseShipmentPerformaceState.loader.next(ProcessingState.Loading);
    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: context.preset as DateRangePresetType,
        date_from: context.range.start,
        date_to: context.range.end,
        interval: context.interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.OVER_ALL_SHIPPING_PERFORMANCE,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap(response => {
          const data = this.extractResponseList(response);
          this.groupedDataStoreService.processEntityGroupedData(
            this.stateWiseShipmentPerformaceState,
            data,
            'state',
            'overallShippingPerformance'
          );
        }),
        catchError(error => {
          console.error(`Error loading state-wise performance data:`, error);
          this.stateWiseShipmentPerformaceState.loader.next(ProcessingState.Error);
          this.handleError('Error loading state-wise shipping performance', error);
          return of({ list: [] });
        })
      );
  }

  updateStatePerformanceDefaultTab(tabId: string): void {
    this.stateWiseShipmentPerformaceState.defaultTab.next(tabId);
    this.groupedDataStoreService.updatePagination(this.stateWiseShipmentPerformaceState, tabId, 1);
  }

  updateStatePerformancePagination(tabId: string, page: number, limit: number = 8): void {
    this.groupedDataStoreService.updatePagination(
      this.stateWiseShipmentPerformaceState,
      tabId,
      page,
      limit
    );
  }

  private extractResponseList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      if (response.list && Array.isArray(response.list)) {
        return response.list;
      } else if (response.stats && Array.isArray(response.stats)) {
        return response.stats;
      } else if (response.content?.list && Array.isArray(response.content.list)) {
        return response.content.list;
      }
    }
    console.warn('Could not extract list from response:', response);
    return [];
  }

  private handleError(message: string, error: any): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    this.errorMessage.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }
}
